import React from 'react';
import { useMealPlanDiscount } from 'src/pages/consumer/meal-plans/useMealPlanDiscount';
import { ArrowSoftLeftV2 } from 'src/shared/design-system/Icon';
import { match } from 'ts-pattern';
import { DEFAULT_FEATURED_SHEFS } from './ShefTrio';
import { SignUpGateLayout } from './SignUpGateLayout';

/** Both post-SSO states draw one screen, so five form types map to four. */
export enum GateScreen {
  ENTRY = 'ENTRY',
  SIGN_UP = 'SIGN_UP',
  LOGIN = 'LOGIN',
  POST_SSO = 'POST_SSO',
}

interface Props {
  screen: GateScreen;
  onBack: () => void;
  children: React.ReactNode;
}

const BackAffordance: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <button
    type='button'
    onClick={onBack}
    aria-label='Back'
    className='flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-eggplant'>
    <ArrowSoftLeftV2 className='w-4' />
    <span className='text-caption-500'>back</span>
  </button>
);

/**
 * The trio is fixed to {@link DEFAULT_FEATURED_SHEFS} rather than resolved per region the way
 * control is. Swapping a full-viewport hero's faces by zip code is a merchandising call nobody has
 * made.
 */
export const SignUpGateScreen: React.FC<Props> = ({ screen, onBack, children }) => {
  const { totaledDiscountText } = useMealPlanDiscount();

  const claimHeadline = (
    <>
      Claim <span className='text-mint-400'>{totaledDiscountText} off</span> &amp; unlock your matches
    </>
  );

  const props = match(screen)
    .with(GateScreen.ENTRY, () => ({
      headline: claimHeadline,
      shefs: DEFAULT_FEATURED_SHEFS,
      showPerks: true,
      showChecker: true,
    }))
    .with(GateScreen.SIGN_UP, () => ({
      headline: claimHeadline,
      subhead: 'Sign up to see your personalized menu',
    }))
    .with(GateScreen.LOGIN, () => ({
      headline: 'Welcome back!',
      subhead: 'Your personalized menu awaits',
      shefs: DEFAULT_FEATURED_SHEFS,
      backAction: <BackAffordance onBack={onBack} />,
    }))
    .with(GateScreen.POST_SSO, () => ({
      headline: claimHeadline,
      subhead: 'Sign up to see your personalized menu',
    }))
    .exhaustive();

  return <SignUpGateLayout {...props}>{children}</SignUpGateLayout>;
};
